import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink, User, Users, Book, Utensils, Heart, Droplets, Coffee, Calendar, Clock, MapPin, Send, MessageCircle } from 'lucide-react';
import { SEO } from '../components/SEO';
import AnimatedSection from '../components/AnimatedSection';
import RequestModal from '../components/RequestModal';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface Request {
  id: string;
  name: string | null;
  type: 'prayer' | 'praise';
  description: string;
  created_at: string;
}

const PrayerFasting: React.FC = () => {
  const [expandedFast, setExpandedFast] = useState<string | null>(null);
  const [expandedVerse, setExpandedVerse] = useState<string | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requests, setRequests] = useState<Request[]>([]);
  const [userPrayers, setUserPrayers] = useState<Set<string>>(new Set());
  const [showAllRequests, setShowAllRequests] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchRequests();
    loadUserPrayers();
  }, []);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('requests')
        .select('*')
        .order('prayer_count', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      // Ensure prayer_count is properly set, defaulting to 0 if null
      const requestsWithCount = (data || []).map(request => ({
        ...request,
        prayer_count: request.prayer_count || 0
      }));
      
      setRequests(requestsWithCount);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const loadUserPrayers = () => {
    const stored = localStorage.getItem('user_prayers');
    if (stored) {
      setUserPrayers(new Set(JSON.parse(stored)));
    }
  };

  const handlePrayerClick = async (requestId: string) => {
    try {
      // Increment prayer count in database
      const { error } = await supabase.rpc('increment_prayer_count', {
        request_id: requestId
      });

      if (error) throw error;

      // Mark as prayed for this user locally
      const newUserPrayers = new Set(userPrayers);
      newUserPrayers.add(requestId);
      setUserPrayers(newUserPrayers);
      localStorage.setItem('user_prayers', JSON.stringify([...newUserPrayers]));
      
      // Refresh requests to show updated count
      await fetchRequests();
      
      toast.success('Thank you for praying! 🙏', {
        duration: 2000,
        style: {
          background: '#10B981',
          color: 'white',
        },
      });
    } catch (error) {
      console.error('Error updating prayer count:', error);
      toast.error('Failed to record prayer. Please try again.');
    }
  };

  // Determine how many cards to show initially
  const getInitialCardCount = () => {
    // Mobile: 1 card, Desktop: 3 cards (one row)
    return window.innerWidth < 768 ? 1 : 3;
  };

  const [initialCardCount] = useState(getInitialCardCount());
  const visibleRequests = showAllRequests ? requests : requests.slice(0, initialCardCount);
  const hasMoreRequests = requests.length > initialCardCount;

  const fastTypes = [
    {
      id: 'daniel',
      title: 'Daniel',
      icon: <Utensils className="w-5 h-5" />,
      link: 'https://ultimatedanielfast.com/ultimate-daniel-fast-food-guidelines/',
      details: 'A partial fast focusing on vegetables, fruits, and water while avoiding meat, sweets, and processed foods. Based on Daniel\'s choice to eat only vegetables and drink water.'
    },
    {
      id: 'esther',
      title: 'Esther',
      icon: <Heart className="w-5 h-5" />,
      link: 'https://en.m.wikipedia.org/wiki/Fast_of_Esther',
      details: 'A complete fast from food and water, typically lasting 3 days. Based on Esther\'s call for the Jews to fast before she approached the king.'
    },
    {
      id: 'water',
      title: 'Water/Calorie Free',
      icon: <Droplets className="w-5 h-5" />,
      details: 'Complete abstinence from food, consuming only water. Just like Jesus and Moses did for 40 days. This is the most intensive form of fasting.'
    },
    {
      id: 'liquid',
      title: 'Liquid',
      icon: <Coffee className="w-5 h-5" />,
      details: 'Consuming no solid food but allowing liquids with nutrients such as juice, smoothies, and shakes. Provides some nutrition while still practicing discipline.'
    }
  ];

  const verses = [
    { 
      ref: 'Duet 9:18', 
      type: 'private', 
      description: '(Moses); petition', 
      context: 'Moses fasted and prayed for Israel after the golden calf incident',
      verse: 'And I fell down before the Lord, as at the first, forty days and forty nights. I neither ate bread nor drank water, because of all the sin that you had committed, in doing what was evil in the sight of the Lord to provoke him to anger.'
    },
    { 
      ref: 'Judge 20:25', 
      type: 'corporate', 
      description: 'repenting grieving', 
      context: 'Israel fasted in grief and repentance during civil war',
      verse: 'And all the people of Israel, the whole army, went up and came to Bethel and wept. They sat there before the Lord and fasted that day until evening, and offered burnt offerings and peace offerings before the Lord.'
    },
    { 
      ref: '1 Sam 7:3', 
      type: 'corporate', 
      description: 'set your heart in worship', 
      context: 'Samuel called Israel to fast and worship at Mizpah',
      verse: 'And Samuel said to all the house of Israel, "If you are returning to the Lord with all your heart, then put away the foreign gods and the Ashtaroth from among you and direct your heart to the Lord and serve him only, and he will deliver you out of the hand of the Philistines."'
    },
    { 
      ref: '2 Sam 1:12', 
      type: 'corporate', 
      description: 'lament & grief', 
      context: 'David and his men fasted in mourning for Saul and Jonathan',
      verse: 'And they mourned and wept and fasted until evening for Saul and for Jonathan his son and for the people of the Lord and for the house of Israel, because they had fallen by the sword.'
    },
    { 
      ref: '2 Sam 12:16', 
      type: 'private', 
      description: 'petition', 
      context: 'David fasted and pleaded with God for his sick child',
      verse: 'David therefore sought God on behalf of the child. And David fasted and went in and lay all night on the ground.'
    },
    { 
      ref: 'Ezra 8:21', 
      type: 'corporate', 
      description: 'petition', 
      context: 'Ezra proclaimed a fast to seek God\'s protection on their journey',
      verse: 'Then I proclaimed a fast there, at the river Ahava, that we might humble ourselves before our God, to seek from him a safe journey for ourselves, our children, and all our goods.'
    },
    { 
      ref: 'Nehemiah 1:4', 
      type: 'private', 
      description: 'lament petition', 
      context: 'Nehemiah fasted and prayed upon hearing of Jerusalem\'s condition',
      verse: 'As soon as I heard these words I sat down and wept and mourned for days, and I continued fasting and praying before the God of heaven.'
    },
    { 
      ref: 'Ester 4:16', 
      type: 'corporate', 
      description: 'petition', 
      context: 'Esther called for all Jews to fast before she approached the king',
      verse: 'Go, gather all the Jews to be found in Susa, and hold a fast on my behalf, and do not eat or drink for three days, night or day. I and my young women will also fast as you do. Then I will go to the king, though it is against the law, and if I perish, I perish.'
    },
    { 
      ref: 'Daniel 9:3', 
      type: 'private', 
      description: 'repentance', 
      context: 'Daniel fasted while confessing Israel\'s sins and seeking God',
      verse: 'Then I turned my face to the Lord God, seeking him by prayer and pleas for mercy with fasting and sackcloth and ashes.'
    },
    { 
      ref: 'Matthew 4', 
      type: 'private', 
      description: 'Jesus fasts to show closeness and for training', 
      context: 'Jesus fasted 40 days in the wilderness before His ministry',
      verse: 'And after fasting forty days and forty nights, he was hungry. And the tempter came and said to him, "If you are the Son of God, command these stones to become loaves of bread."'
    },
    { 
      ref: 'Matthew 6', 
      type: 'teaching', 
      description: 'Jesus says it\'s for God', 
      context: 'Jesus teaches about proper motives and methods for fasting',
      verse: 'And when you fast, do not look gloomy like the hypocrites, for they disfigure their faces that their fasting may be seen by others. Truly, I say to you, they have received their reward. But when you fast, anoint your head and wash your face, that your fasting may not be seen by others but by your Father who is in secret. And your Father who sees in secret will reward you.'
    },
    { 
      ref: 'Acts 13', 
      type: 'corporate', 
      description: 'worship & petition', 
      context: 'The church at Antioch fasted while worshiping and seeking God\'s will',
      verse: 'While they were worshiping the Lord and fasting, the Holy Spirit said, "Set apart for me Barnabas and Saul for the work to which I have called them."'
    },
    { 
      ref: 'Acts 14', 
      type: 'corporate', 
      description: 'making space & petition', 
      context: 'Paul and Barnabas fasted when appointing elders in churches',
      verse: 'And when they had appointed elders for them in every church, with prayer and fasting they committed them to the Lord in whom they had believed.'
    }
  ];

  const privateVerses = verses.filter(v => v.type === 'private');
  const corporateVerses = verses.filter(v => v.type === 'corporate');
  const teachingVerses = verses.filter(v => v.type === 'teaching');

  const toggleFastExpansion = (fastId: string) => {
    setExpandedFast(expandedFast === fastId ? null : fastId);
  };

  const toggleVerseExpansion = (verseRef: string) => {
    setExpandedVerse(expandedVerse === verseRef ? null : verseRef);
  };

  return (
    <>
      <SEO 
        title="Prayer & Fasting | Monday Night Group"
        description="Join us for Prayer & Fasting August 4-8, 2025, 6:00-7:00 AM CST. A voluntary time of spiritual discipline, drawing near to God through prayer and fasting."
        image="https://images.pexels.com/photos/8468/food-salad-healthy-lunch.jpg?auto=compress&cs=tinysrgb&w=1200&h=630"
        url="https://mondaynightgroup.com/prayer-fasting"
      />

      {/* Hero Section */}
      <motion.section 
        className="relative min-h-[100vh] sm:h-[60vh] bg-gradient-to-r from-blue-900 to-blue-700 flex items-center overflow-hidden"
        style={{ 
          backgroundImage: 'url(https://images.pexels.com/photos/8468/food-salad-healthy-lunch.jpg?auto=compress&cs=tinysrgb&w=1600)', 
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-blue-900/90"></div>
        
        {/* Decorative Elements */}
        <motion.div 
          className="absolute top-10 right-10 w-32 h-32 border border-white/20 rounded-full hidden md:block"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute bottom-20 left-20 w-16 h-16 border border-white/10 rounded-full hidden md:block"
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />
        
        <div className="container mx-auto px-3 sm:px-6 z-10">
          <div className="max-w-4xl mx-auto text-center py-8 sm:py-0">
            {/* Date & Time Badge */}
            <motion.div 
              className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1 sm:px-3 sm:py-2 mb-2 sm:mb-3 text-xs"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Calendar className="w-3 h-3 text-white mr-1" />
              <span className="text-white font-medium mr-2 text-xs">August 4 - 8, 2025</span>
              <Clock className="w-3 h-3 text-white mr-1" />
              <span className="text-white font-medium">6:00 - 7:00 AM CST</span>
            </motion.div>
            
            {/* Address Badge */}
            <motion.div 
              className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1 sm:px-3 sm:py-2 mb-3 sm:mb-4 hover:bg-white/20 transition-colors cursor-pointer text-xs max-w-[90%] mx-auto"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              onClick={() => window.open('https://www.google.com/maps/search/?api=1&query=3534+West+End+Ave,+Nashville,+TN+37205', '_blank')}
            >
              <MapPin className="w-3 h-3 text-white mr-1 flex-shrink-0" />
              <span className="text-white font-medium truncate">
                3534 West End Ave, Nashville, TN 37205
              </span>
            </motion.div>
            
            <motion.h1 
              className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3 sm:mb-4 drop-shadow-2xl px-1"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Prayer & Fasting
            </motion.h1>
            
            <motion.div
              className="flex items-center justify-center mb-6"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <div className="h-px bg-gradient-to-r from-transparent via-white/50 to-transparent w-20 sm:w-32"></div>
              <div className="mx-4 w-3 h-3 bg-white/70 rounded-full"></div>
              <div className="h-px bg-gradient-to-r from-transparent via-white/50 to-transparent w-20 sm:w-32"></div>
            </motion.div>
            
            <motion.p 
              className="text-sm sm:text-lg md:text-xl text-white/90 font-light leading-relaxed max-w-2xl mx-auto mb-4 sm:mb-6 px-2"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              A voluntary time of spiritual discipline, drawing near to God through prayer and fasting as you feel led
            </motion.p>
            
            <motion.div
              className="mt-4 sm:mt-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.1 }}
            >
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
                <a
                  href="https://meet.google.com/ipk-kzcy-xaj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-600/90 backdrop-blur-sm border border-green-500/30 rounded-full px-3 sm:px-6 py-2 sm:py-3 text-white font-medium hover:bg-green-700/90 transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm"
                >
                  <svg className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15,9H9V15H15M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                  </svg>
                  Join Virtual Meeting
                </a>
                <button
                  onClick={() => setIsRequestModalOpen(true)}
                  className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 sm:px-6 py-2 sm:py-3 text-white font-medium hover:bg-white/20 transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm"
                >
                  <Send className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-3" />
                  Submit Prayer & Praise Requests
                </button>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Floating particles effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, -100],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </motion.section>

      {/* Prayer Requests Carousel */}
      {requests.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-blue-50 to-white" id="prayer-requests">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Community Prayer Requests</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Join our community in prayer. Click the 🙏 button to let others know you've prayed for these requests.
              </p>
            </div>
            
            {/* Prayer Requests Grid */}
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleRequests.map((request, index) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="h-full"
                  >
                    <div className={`relative bg-white rounded-xl shadow-lg p-6 border-l-4 ${
                      request.type === 'prayer' 
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-white' 
                        : 'border-green-500 bg-gradient-to-br from-green-50 to-white'
                    } transform hover:scale-105 transition-all duration-300 flex flex-col h-full`}>
                      {/* Type Badge */}
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4 w-fit ${
                        request.type === 'prayer'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {request.type === 'prayer' ? '🙏' : '🙌'} {request.type === 'prayer' ? 'Prayer' : 'Praise'}
                      </div>
                      
                      {/* Name */}
                      <h3 className="font-bold text-gray-900 mb-3" style={{ fontFamily: 'Kalam, cursive' }}>
                        {request.name || 'Anonymous'}
                      </h3>
                      
                      {/* Description */}
                      <div 
                        className="text-gray-700 text-sm leading-relaxed mb-4 flex-grow whitespace-pre-wrap"
                        style={{ fontFamily: 'Kalam, cursive' }}
                      >
                        <p>{request.description}</p>
                      </div>
                      
                      {/* Footer with Date and Prayer Button */}
                      <div className="mt-auto">
                        {/* Date */}
                        <p className="text-xs text-gray-500 mb-4" style={{ fontFamily: 'Kalam, cursive' }}>
                          {new Date(request.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      
                        {/* Prayer Button */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => handlePrayerClick(request.id)}
                            disabled={userPrayers.has(request.id)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                              userPrayers.has(request.id)
                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                : 'bg-white shadow-md hover:shadow-lg hover:scale-110 text-gray-700 hover:text-blue-600'
                            }`}
                          >
                            <span className="text-lg">🙏</span>
                            <span className="font-bold">{request.prayer_count || 0}</span>
                          </button>
                        </div>
                      </div>
                      
                      {userPrayers.has(request.id) && (
                        <div className="absolute top-4 right-4 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Prayed ✓
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* See More Button */}
              {hasMoreRequests && !showAllRequests && (
                <div className="text-center mt-8">
                  <motion.button
                    onClick={() => setShowAllRequests(true)}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    See More Prayer Requests ({requests.length - initialCardCount} more)
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12 sm:space-y-16">
            
            {/* Fasting vs Spiritual Discipline */}
            <AnimatedSection delay={0.2}>
              <motion.div 
                className="bg-blue-50 rounded-xl p-6 sm:p-8 md:p-12 shadow-lg"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6">Fasting vs Spiritual Discipline</h2>
                <p className="text-gray-700 text-base sm:text-lg mb-6 leading-relaxed">
                  Fasting is about food and voluntarily giving up something God ordained good, for something better… Him.
                </p>
                <p className="text-gray-700 text-base sm:text-lg mb-6 leading-relaxed">
                  We fast to draw near to our Father and Jesus. It is also a way to become more obedient to our spirit. Here are three reasons we fast:
                </p>
                <div className="space-y-4">
                  {[
                    { icon: '🎯', title: 'Training', desc: 'presence over comfort' },
                    { icon: '🙏', title: 'Petition', desc: 'physically asking God to fill a space' },
                    { icon: '❤️', title: 'Yearning', desc: 'hope-filled hunger for the Kingdom' }
                  ].map((reason, index) => (
                    <motion.div 
                      key={reason.title}
                      className="flex items-start bg-white p-4 rounded-lg shadow-sm"
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      whileHover={{ x: 10, backgroundColor: '#f8fafc' }}
                    >
                      <span className="text-2xl mr-4">{reason.icon}</span>
                      <div>
                        <span className="font-semibold text-gray-900 text-base sm:text-lg">{reason.title}</span>
                        <span className="text-gray-700 ml-2">- {reason.desc}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatedSection>

            {/* Types of Biblical Fasts */}
            <AnimatedSection delay={0.4}>
              <motion.div 
                className="bg-green-50 rounded-xl p-6 sm:p-8 border-l-4 border-green-600"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-start mb-6">
                  <div className="bg-green-600 text-white p-3 rounded-full mb-4 sm:mb-0 sm:mr-4">
                    📋
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center sm:text-left">Types of Biblical Fasts</h2>
                </div>
                <div className="space-y-4">
                  {fastTypes.map((fast, index) => (
                    <motion.div 
                      key={fast.id}
                      className="bg-white rounded-lg shadow-sm overflow-hidden"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                    >
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleFastExpansion(fast.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col sm:flex-row sm:items-center">
                            <div className="text-green-600 mr-3">
                              {fast.icon}
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-0">{fast.title}</h3>
                            {fast.link && (
                              <>
                                <a 
                                  href={fast.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="sm:ml-4 inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors bg-blue-100 hover:bg-blue-200 px-3 py-1 rounded-full text-sm font-medium"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="mr-1">Learn More</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </>
                            )}
                          </div>
                          <motion.div
                            animate={{ rotate: expandedFast === fast.id ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          </motion.div>
                        </div>
                      </div>
                      <AnimatePresence>
                        {expandedFast === fast.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-gray-100"
                          >
                            <div className="p-4 bg-gray-50">
                              <p className="text-gray-700 leading-relaxed">{fast.details}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatedSection>

            {/* Fasting verses */}
            <AnimatedSection delay={0.6}>
              <motion.div 
                className="bg-purple-50 rounded-xl p-6 sm:p-8 border-l-4 border-purple-600"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-start mb-6 sm:mb-8">
                  <div className="bg-purple-600 text-white p-3 rounded-full mb-4 sm:mb-0 sm:mr-4">
                    📖
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center sm:text-left">Fasting Verses</h2>
                </div>
                
                {/* Private Fasts */}
                <div className="mb-6 sm:mb-8">
                  <div className="flex items-center mb-4">
                    <User className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Private Fasts</h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {privateVerses.map((verse, index) => (
                      <motion.div
                        key={verse.ref}
                        className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all border-l-4 border-blue-400 overflow-hidden"
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.8 + index * 0.05 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <div 
                          className="p-4 cursor-pointer"
                          onClick={() => toggleVerseExpansion(verse.ref)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-gray-900 mb-1">{verse.ref}</div>
                            </div>
                            <motion.div
                              animate={{ rotate: expandedVerse === verse.ref ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </motion.div>
                          </div>
                        </div>
                        <AnimatePresence>
                          {expandedVerse === verse.ref && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="border-t border-gray-100"
                            >
                              <div className="p-4 bg-blue-50">
                                <p className="text-gray-700 italic leading-relaxed">"{verse.verse}"</p>
                                <p className="text-xs text-gray-500 mt-2">- ESV</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Corporate Fasts */}
                <div className="mb-6 sm:mb-8">
                  <div className="flex items-center mb-4">
                    <Users className="w-5 h-5 text-green-600 mr-2" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Corporate Fasts</h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {corporateVerses.map((verse, index) => (
                      <motion.div
                        key={verse.ref}
                        className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all border-l-4 border-green-400 overflow-hidden"
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 1.0 + index * 0.05 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <div 
                          className="p-4 cursor-pointer"
                          onClick={() => toggleVerseExpansion(verse.ref)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-gray-900 mb-1">{verse.ref}</div>
                            </div>
                            <motion.div
                              animate={{ rotate: expandedVerse === verse.ref ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </motion.div>
                          </div>
                        </div>
                        <AnimatePresence>
                          {expandedVerse === verse.ref && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="border-t border-gray-100"
                            >
                              <div className="p-4 bg-green-50">
                                <p className="text-gray-700 italic leading-relaxed">"{verse.verse}"</p>
                                <p className="text-xs text-gray-500 mt-2">- ESV</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Teaching Verses */}
                <div>
                  <div className="flex items-center mb-4">
                    <Book className="w-5 h-5 text-purple-600 mr-2" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Jesus' Teaching</h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {teachingVerses.map((verse, index) => (
                      <motion.div
                        key={verse.ref}
                        className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all border-l-4 border-purple-400 overflow-hidden"
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 1.2 + index * 0.05 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <div 
                          className="p-4 cursor-pointer"
                          onClick={() => toggleVerseExpansion(verse.ref)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-gray-900 mb-1">{verse.ref}</div>
                            </div>
                            <motion.div
                              animate={{ rotate: expandedVerse === verse.ref ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </motion.div>
                          </div>
                        </div>
                        <AnimatePresence>
                          {expandedVerse === verse.ref && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="border-t border-gray-100"
                            >
                              <div className="p-4 bg-purple-50">
                                <p className="text-gray-700 italic leading-relaxed">"{verse.verse}"</p>
                                <p className="text-xs text-gray-500 mt-2">- ESV</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatedSection>

          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">Important Disclaimer</h3>
                  <div className="text-yellow-700 space-y-3">
                    <p>
                      <strong>Voluntary Participation:</strong> Fasting is a personal spiritual discipline and participation is entirely voluntary. Please pray and seek God's guidance about what type of fast, if any, is right for you during this time.
                    </p>
                    <p>
                      <strong>Health Considerations:</strong> Before beginning any fast, especially extended fasts or if you have health conditions, please consult with your healthcare provider. This is particularly important if you have diabetes, eating disorders, are pregnant, nursing, taking medications, or have any chronic health conditions.
                    </p>
                    <p>
                      <strong>Listen to Your Body:</strong> If you experience dizziness, weakness, or any concerning symptoms while fasting, please break your fast and seek medical attention if needed. Your health and well-being are important to God and to our community.
                    </p>
                    <p>
                      <strong>Alternative Forms:</strong> Remember that fasting can take many forms beyond food - consider fasting from social media, entertainment, or other activities to create space for prayer and reflection.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <RequestModal 
        isOpen={isRequestModalOpen} 
        onClose={() => setIsRequestModalOpen(false)} 
      />
    </>
  );
};

export default PrayerFasting;